import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save } from 'lucide-react';
import { MarketTypeLocationFields } from '@/pages/price-benchmarking/components/MarketTypeLocationFields';
import { ProductNameField } from '@/pages/price-benchmarking/components/ProductNameField';
import { DatePickerField } from '@/pages/price-benchmarking/components/DatePickerField';
import { FMCGProductField } from './FMCGProductField';
import { LOCATION_OPTIONS } from '@/pages/price-benchmarking/constants';
import type { CategoryType } from '@/pages/BenchmarkWebApp';

interface FormData {
  marketType: string;
  productName: string;
  price: string;
  location: string;
  productRemark: string;
  productCategory: string;
}

interface UserProfile {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
}

interface BenchmarkFormProps {
  userProfile: UserProfile;
  formData: FormData;
  date: Date;
  isSubmitting: boolean;
  category: CategoryType;
  onCategoryChange: (category: CategoryType) => void;
  onInputChange: (field: string, value: string) => void;
  onDateChange: (date: Date) => void;
  onSubmit: () => void;
}

// FMCG market types with their locations
const FMCG_MARKET_TYPES = [
  { value: 'local-shops', label: 'Local Shops' },
  { value: 'supermarket', label: 'Supermarket' },
  { value: 'merkato', label: 'Merkato' },
];

export const BenchmarkForm: React.FC<BenchmarkFormProps> = ({
  userProfile,
  formData,
  date,
  isSubmitting,
  category,
  onCategoryChange,
  onInputChange,
  onDateChange,
  onSubmit
}) => {
  // Get locations based on selected FMCG market type
  const getFMCGLocations = () => {
    if (!formData.marketType) return [];
    return LOCATION_OPTIONS[formData.marketType as keyof typeof LOCATION_OPTIONS] || [];
  };

  return (
    <div className="min-h-screen bg-background text-foreground p-4">
      <div className="container max-w-2xl mx-auto space-y-6">
        <Card>
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-2">
              📊 Price Data Entry
            </CardTitle>
            <CardDescription>
              Welcome, {userProfile?.first_name || 'Benchmarker'}!
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Category Tabs */}
            <Tabs value={category} onValueChange={(v) => onCategoryChange(v as CategoryType)}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="fruits-vegetables">🥬 Fruits & Vegetables</TabsTrigger>
                <TabsTrigger value="fmcg">🛒 FMCG</TabsTrigger>
              </TabsList>

              {/* Fruits & Vegetables Tab */}
              <TabsContent value="fruits-vegetables" className="space-y-6 mt-6">
                {/* Market Type and Location */}
                <MarketTypeLocationFields
                  marketType={formData.marketType}
                  location={formData.location}
                  onMarketTypeChange={(value) => onInputChange('marketType', value)}
                  onLocationChange={(value) => onInputChange('location', value)}
                />

                {/* Product Name */}
                <ProductNameField
                  productName={formData.productName}
                  onProductNameChange={(value) => onInputChange('productName', value)}
                />

                {/* Price */}
                <div className="space-y-2">
                  <Label htmlFor="price">Price (ETB per kg) *</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="price"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={formData.price}
                      onChange={(e) => onInputChange('price', e.target.value)}
                      required
                      className="flex-1"
                    />
                    <div className="bg-muted border border-input rounded-md px-3 py-2 text-sm font-medium min-w-[60px] text-center">
                      ETB
                    </div>
                  </div>
                </div>

                {/* Date */}
                <DatePickerField
                  date={date}
                  onDateChange={onDateChange}
                />

                {/* Product Remark */}
                <div className="space-y-2">
                  <Label htmlFor="productRemark">Remarks (Optional)</Label>
                  <Textarea
                    id="productRemark"
                    placeholder="Any additional notes about the product or price"
                    value={formData.productRemark}
                    onChange={(e) => onInputChange('productRemark', e.target.value)}
                    rows={3}
                  />
                </div>
              </TabsContent>

              {/* FMCG Tab */}
              <TabsContent value="fmcg" className="space-y-6 mt-6">
                {/* Market Type */}
                <div className="space-y-2">
                  <Label>Market Type *</Label>
                  <Select
                    value={formData.marketType}
                    onValueChange={(value) => {
                      onInputChange('marketType', value);
                      onInputChange('location', ''); // Reset location when market type changes
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select market type" />
                    </SelectTrigger>
                    <SelectContent>
                      {FMCG_MARKET_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Location - only show if not Merkato */}
                {formData.marketType && formData.marketType !== 'merkato' && (
                  <div className="space-y-2">
                    <Label>Location *</Label>
                    <Select
                      value={formData.location}
                      onValueChange={(value) => onInputChange('location', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        {getFMCGLocations().map((loc) => (
                          <SelectItem key={loc} value={loc}>
                            {loc}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Product Name - Searchable from products table */}
                <FMCGProductField
                  productName={formData.productName}
                  onProductNameChange={(value) => onInputChange('productName', value)}
                />

                {/* Price */}
                <div className="space-y-2">
                  <Label htmlFor="fmcgPrice">Price (ETB per unit) *</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="fmcgPrice"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={formData.price}
                      onChange={(e) => onInputChange('price', e.target.value)}
                      required
                      className="flex-1"
                    />
                    <div className="bg-muted border border-input rounded-md px-3 py-2 text-sm font-medium min-w-[60px] text-center">
                      ETB
                    </div>
                  </div>
                </div>

                {/* Date */}
                <DatePickerField
                  date={date}
                  onDateChange={onDateChange}
                />

                {/* Product Remark */}
                <div className="space-y-2">
                  <Label htmlFor="fmcgProductRemark">Remarks (Optional)</Label>
                  <Textarea
                    id="fmcgProductRemark"
                    placeholder="Any additional notes (brand, size, etc.)"
                    value={formData.productRemark}
                    onChange={(e) => onInputChange('productRemark', e.target.value)}
                    rows={3}
                  />
                </div>
              </TabsContent>
            </Tabs>

            {/* Submit Button (fallback for non-Telegram environments) */}
            {!window.Telegram?.WebApp && (
              <Button 
                type="submit" 
                className="w-full gap-2" 
                disabled={isSubmitting}
                onClick={onSubmit}
              >
                <Save className="h-4 w-4" />
                {isSubmitting ? 'Submitting...' : 'Submit Price Data'}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
